import { groupWith } from 'ramda';
import {
  IAnalysDetailTableData,
  IDetailRow,
  IDetailCell,
  FieldType,
  IColumnHeader,
} from '../../EkonomiskAnalysDetailTable/EkonomiskAnalysDetailTable';
import mergeSort, {
  stringCompareNumeric,
} from '../../../../../../../../Utils/mergeSort';
import { ITEDAvenyEKAnalysColumnData } from '../../../Queries/EkonomiskAnalysDetailQuery/EkonomiskAnalysDetailQuery';
import { ISortProperty } from '../../../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import {
  SummationModeType,
  BudgetSummationModeType,
} from '../../../Utils/SummationModeUtils';

const cellvalueSum = (
  valueA: string | null,
  valueB: string | null
): string | null => {
  if (valueA == null || valueB == null) {
    return null;
  } else {
    return (
      Number.parseFloat(valueA || '0') + Number.parseFloat(valueB || '0')
    ).toString();
  }
};

const cellsSum = (
  cellsA: IDetailCell[],
  cellsB: IDetailCell[]
): IDetailCell[] =>
  cellsA.map((cell, index) => {
    if (
      cell.fieldType === FieldType.debet ||
      cell.fieldType === FieldType.kredit ||
      cell.fieldType === FieldType.saldo ||
      cell.fieldType === FieldType.antal
    ) {
      return {
        ...cell,
        value: cellvalueSum(cell.value, cellsB[index].value),
      };
    } else {
      return cell;
    }
  });

const detailRowSumReducer = (
  accumulator: IDetailRow,
  currentValue: IDetailRow
): IDetailRow => {
  return {
    definitivbokford: accumulator.definitivbokford,
    id: accumulator.id,
    journalNr: accumulator.journalNr,
    bokfDatum: accumulator.bokfDatum,
    verifikationId: accumulator.verifikationId,
    verifikationNr: accumulator.verifikationNr,
    cells: cellsSum(accumulator.cells, currentValue.cells),
  };
};

export const sumDetails = (
  details: IAnalysDetailTableData,
  summationMode: SummationModeType
): IAnalysDetailTableData => {
  if (summationMode.value === 'journal') {
    const sortedRows = mergeSort(details.tableData, (a, b) =>
      stringCompareNumeric(a.journalNr, b.journalNr)
    );
    const groupedDetails = groupWith<IDetailRow>(
      (a, b) =>
        a.journalNr === b.journalNr &&
        stringCompareNumeric(a.journalNr, b.journalNr) === 0,
      sortedRows
    );
    const summedDetails = groupedDetails.map(details =>
      details.reduce(detailRowSumReducer)
    );

    return { ...details, tableData: summedDetails };
  } else if (summationMode.value === 'verifikation') {
    const sortedRows = mergeSort(details.tableData, (a, b) =>
      a.verifikationNr.localeCompare(b.verifikationNr)
    );
    const groupedDetails = groupWith<IDetailRow>(
      (a, b) =>
        a.verifikationNr === b.verifikationNr && a.journalNr === b.journalNr,
      sortedRows
    );
    const summedDetails = groupedDetails.map(details =>
      details.reduce(detailRowSumReducer)
    );

    return { ...details, tableData: summedDetails };
  }

  return details;
};

export const sumBudgetDetails = (
  details: IAnalysDetailTableData,
  summationMode: BudgetSummationModeType
): IAnalysDetailTableData => {
  // if (summationMode.value === 'budgetRad') {
  // const sortedRows = mergeSort(details.tableData, (a, b) =>
  //   stringCompare(a.journalNr, b.journalNr)
  // );
  // const groupedDetails = groupWith<IDetailRow>(
  //   (a, b) =>
  //     a.journalNr === b.journalNr &&
  //     stringCompare(a.journalNr, b.journalNr) === 0,
  //   sortedRows
  // );
  // const summedDetails = groupedDetails.map(details =>
  //   details.reduce(detailRowSumReducer)
  // );

  // return { ...details, tableData: summedDetails };

  // }
  if (summationMode.value === 'budgetVerifikation') {
    const sortedRows = mergeSort(details.tableData, (a, b) =>
      a.verifikationNr.localeCompare(b.verifikationNr)
    );
    const groupedDetails = groupWith<IDetailRow>(
      (a, b) => a.verifikationNr === b.verifikationNr,
      sortedRows
    );
    const summedDetails = groupedDetails.map(groupedDetail => {
      const adjustedDetails = groupedDetail.map(detail => {
        return {
          ...detail,
          cells: detail.cells.map(cell => {
            const headerIndex = details.tableHeader.columnHeaders.findIndex(
              (value, index, object) => {
                return value.id === cell.id;
              }
            );
            if (
              details.tableHeader.columnHeaders[headerIndex].title1 === 'Debet'
            ) {
              cell.fieldType = 4;
            } else if (
              details.tableHeader.columnHeaders[headerIndex].title1 === 'Kredit'
            ) {
              cell.fieldType = 5;
            } else if (
              details.tableHeader.columnHeaders[headerIndex].title1 === 'Saldo'
            ) {
              cell.fieldType = 6;
            }

            return cell;
          }),
        };
      });
      return adjustedDetails.reduce(detailRowSumReducer);
    });

    const summedDetailsObject = {
      ...details,
      tableHeader: {
        ...details.tableHeader,
        columnHeaders: details.tableHeader.columnHeaders.map(header => {
          if (
            header.title1 === 'Anställningsnr' ||
            header.title1 === 'Kommentar' ||
            header.title1 === 'Period' ||
            header.title1 === 'Periodiserad' ||
            header.title1 === 'Enligt kurva'
          ) {
            header.isVisible = false;
          }

          return header;
        }),
      },
      tableData: summedDetails,
    };

    return summedDetailsObject;
  }

  return details;
};

export const getInitialSort = (
  summationMode: SummationModeType,
  isBudgetColumn: boolean,
  lopsaldoDetaljrad: boolean,
  headcells: IColumnHeader[]
): ISortProperty[] => {
  if (isBudgetColumn) {
    const period = headcells.find(
      header => header.title1 === 'Period' && header.isVisible
    );

    if (!period) {
      return [
        {
          caption: '',
          orderBy: 'col13',
          order: 'desc',
        },
        {
          caption: '',
          orderBy: 'col11',
          order: 'desc',
        },
      ];
    } else {
      return [
        {
          caption: '',
          orderBy: 'col16',
          order: 'desc',
        },
        {
          caption: '',
          orderBy: 'col12',
          order: 'desc',
        },
      ];
    }
  }
  switch (summationMode.value) {
    case 'journal':
      if (lopsaldoDetaljrad) {
        return [
          { caption: '', orderBy: 'col11', order: 'asc' },
          {
            caption: '',
            orderBy: 'col5',
            order: 'asc',
          },
        ];
      } else {
        return [
          {
            caption: '',
            orderBy: 'col11',
            order: 'desc',
          },
          {
            caption: '',
            orderBy: 'col5',
            order: 'asc',
          },
        ];
      }
    case 'verifikation':
      if (lopsaldoDetaljrad) {
        return [
          { caption: '', orderBy: 'col11', order: 'asc' },
          {
            caption: '',
            orderBy: 'col4',
            order: 'asc',
          },
        ];
      } else {
        return [
          {
            caption: '',
            orderBy: 'col11',
            order: 'desc',
          },
          {
            caption: '',
            orderBy: 'col4',
            order: 'asc',
          },
        ];
      }
    case 'verifikationsrad':
      if (lopsaldoDetaljrad) {
        return [
          { caption: '', orderBy: 'col11', order: 'asc' },
          {
            caption: '',
            orderBy: 'col4',
            order: 'asc',
          },
          {
            caption: '',
            orderBy: 'col7',
            order: 'asc',
          },
        ];
      } else {
        return [
          {
            caption: '',
            orderBy: 'col11',
            order: 'desc',
          },
          {
            caption: '',
            orderBy: 'col4',
            order: 'asc',
          },
          {
            caption: '',
            orderBy: 'col7',
            order: 'asc',
          },
        ];
      }
  }
};

export const filterDetails = (
  details: ITEDAvenyEKAnalysColumnData[],
  detailSummationMode: SummationModeType
): ITEDAvenyEKAnalysColumnData[] => {
  if (detailSummationMode.value === 'journal') {
    return details.filter(
      detail =>
        detail.fieldtype !== 'vernr' &&
        detail.fieldtype !== 'journaldatum' &&
        detail.fieldtype !== 'lopnr' &&
        detail.fieldtype !== 'vertext' &&
        detail.fieldtype !== 'radtext' &&
        detail.fieldtype !== 'fakturabild' &&
        detail.fieldtype !== 'bokfunderlag' &&
        detail.fieldtype !== 'konteringskod'
    );
  } else if (detailSummationMode.value === 'verifikation') {
    return details.filter(
      detail =>
        detail.fieldtype !== 'journalnr' &&
        detail.fieldtype !== 'journaldatum' &&
        detail.fieldtype !== 'lopnr' &&
        detail.fieldtype !== 'radtext' &&
        detail.fieldtype !== 'konteringskod'
    );
  } else {
    return details.filter(
      detail =>
        detail.fieldtype !== 'journalnr' && detail.fieldtype !== 'journaldatum'
    );
  }
};

const budgetPeriodAndNull = (
  row: IDetailRow,
  periodIndex: number,
  debetIndex: number,
  kreditIndex: number
) =>
  row.cells[periodIndex].value &&
  row.cells[periodIndex].value === '0' &&
  (row.cells[debetIndex].value == null ||
    row.cells[debetIndex].value === '0.00') &&
  (row.cells[kreditIndex].value == null ||
    row.cells[kreditIndex].value === '0.00');

/**
 * Filtrerar ut raden för budgetperioden (period 0) om både kredit och debet är 0
 */
export const filterOutBudgetPerIfNull = (
  tableData: IAnalysDetailTableData
): IAnalysDetailTableData => {
  const periodIndex = tableData.tableHeader.columnHeaders.findIndex(
    header => header.title1 === 'Period'
  );
  const debetIndex = tableData.tableHeader.columnHeaders.findIndex(
    header => header.title1 === 'Debet'
  );
  const kreditIndex = tableData.tableHeader.columnHeaders.findIndex(
    header => header.title1 === 'Kredit'
  );

  if (debetIndex === -1 || kreditIndex === -1 || periodIndex === -1) {
    return tableData;
  }

  const newTableData: IAnalysDetailTableData = {
    ...tableData,
    tableData: tableData.tableData.filter(
      row => !budgetPeriodAndNull(row, periodIndex, debetIndex, kreditIndex)
    ),
  };

  return newTableData;
};
